import React from 'react';
import { Target, Award, Users, TrendingUp } from 'lucide-react';
import './AboutPage.css';

const AboutPage = () => {
  const values = [
    {
      icon: <Target size={32} />,
      title: 'Excellence',
      description: 'We strive for excellence in every engagement, delivering results that exceed expectations.',
    },
    {
      icon: <Award size={32} />,
      title: 'Integrity',
      description: 'We operate with the highest ethical standards, building trust through transparency.',
    },
    {
      icon: <Users size={32} />,
      title: 'Collaboration',
      description: 'We work as partners with our clients, fostering strong relationships built on mutual success.',
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Innovation',
      description: 'We continuously evolve our approaches to stay ahead of industry trends and challenges.',
    },
  ];

  return (
    <div className="about-page" data-testid="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <h1 data-testid="about-hero-title">About Velonity Consulting</h1>
            <p data-testid="about-hero-subtitle">
              Empowering businesses through strategic consulting, exceptional talent acquisition, and comprehensive verification services.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section section">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2 className="section-title" data-testid="story-title">Our Story</h2>
              <p>
                Founded with a vision to transform the way businesses approach growth and talent management, Velonity Consulting has evolved into a trusted partner for organizations across industries.
              </p>
              <p>
                With over a decade of experience, we&apos;ve helped hundreds of companies navigate complex challenges, build exceptional teams, and implement rigorous verification processes that ensure safety and compliance.
              </p>
              <p>
                Our multidisciplinary approach combines deep industry expertise with innovative methodologies, enabling us to deliver tailored solutions that drive sustainable success.
              </p>
            </div>
            <div className="story-image">
              <img 
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&auto=format&fit=crop" 
                alt="Team collaboration"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section section">
        <div className="container">
          <div className="mission-content">
            <div className="mission-card">
              <h2 data-testid="mission-title">Our Mission</h2>
              <p>
                To empower organizations with strategic insights, exceptional talent, and comprehensive verification services that drive growth, ensure compliance, and build lasting success.
              </p>
            </div>
            <div className="mission-card">
              <h2 data-testid="vision-title">Our Vision</h2>
              <p>
                To be the most trusted consulting partner for businesses seeking transformative solutions, recognized for our expertise, integrity, and commitment to client success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="values-title">Our Core Values</h2>
            <p className="section-subtitle">
              The principles that guide everything we do
            </p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card" data-testid={`value-${index}`}>
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" data-testid="team-title">Our Expertise</h2>
            <p className="section-subtitle">
              A team of seasoned professionals with decades of combined experience
            </p>
          </div>
          <div className="team-stats">
            <div className="team-stat">
              <div className="team-stat-number">50+</div>
              <div className="team-stat-label">Expert Consultants</div>
            </div>
            <div className="team-stat">
              <div className="team-stat-number">15+</div>
              <div className="team-stat-label">Industries Served</div>
            </div>
            <div className="team-stat">
              <div className="team-stat-number">500+</div>
              <div className="team-stat-label">Projects Completed</div>
            </div>
            <div className="team-stat">
              <div className="team-stat-number">98%</div>
              <div className="team-stat-label">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="container">
          <div className="about-cta-content">
            <h2 data-testid="about-cta-title">Partner With Us</h2>
            <p>Experience the Velonity difference. Let&apos;s work together to achieve your business goals.</p>
            <a href="/background-check" className="btn btn-accent btn-lg">Get Started Today</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
